import { Trans } from '@lingui/react/macro';
import { BookingProposalRecordList, BookingProposalStatus, BookingProposalType } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { NoddiIcon } from 'noddi-ui-common';
import { NoddiBasicCard, NoddiDummyButton } from 'noddi-ui-web';
import { customerRoutes, formatLicensePlate } from 'noddi-util';
import { useBrandCapabilities } from '../../hooks/useBrandCapabilities';
import { useIsUserGroupMember } from '../../hooks/useIsUserGroupMember';
import { getProposalDateLabel, groupBookingProposalItemsByCar } from '../../utils/bookingProposalDisplay';

type Props = {
  userGroupId: number;
  excludedLicensePlate?: string;
};

const RECALL_TYPES = [BookingProposalType.RECALL_PROPOSAL_WHEEL_CHANGE, BookingProposalType.RECALL_PROPOSAL_TIRE_HOTEL];

export const PendingRecallsCard = ({ userGroupId, excludedLicensePlate }: Props) => {
  const { showsRecalls } = useBrandCapabilities();
  // Booking pages open by slug for any viewer, but proposals are member-only: a non-member gets 403.
  const isMember = useIsUserGroupMember(userGroupId);
  const enabled = showsRecalls && isMember;
  const { data, error } = noddiAsync.useGet({
    type: URLKeys.getPaginatedBookingProposals,
    input: {
      userGroupIds: [userGroupId],
      statuses: [BookingProposalStatus.PENDING],
      types: RECALL_TYPES,
      isVisibleCustomer: true,
      excludedLicensePlate,
      pageIndex: 0,
      pageSize: 10
    },
    queryConfig: { enabled }
  });

  if (!enabled || error) {
    return null;
  }

  const recalls = data?.results ?? [];
  if (recalls.length === 0) {
    return null;
  }

  return (
    <div className='flex flex-col gap-3'>
      <div className='flex flex-col gap-1'>
        <h2 className='text-xl font-semibold text-primary'>
          {recalls.length === 1 ? (
            <Trans>You have another suggested booking</Trans>
          ) : (
            <Trans>You have other suggested bookings</Trans>
          )}
        </h2>
        <p className='text-sm text-muted-foreground'>
          <Trans>Want to book these too while you're here?</Trans>
        </p>
      </div>
      <ul className='flex flex-col gap-3'>
        {recalls.map((proposal) => (
          <li key={proposal.slug}>
            <PendingRecallItem proposal={proposal} />
          </li>
        ))}
      </ul>
    </div>
  );
};

const PendingRecallItem = ({ proposal }: { proposal: BookingProposalRecordList }) => {
  const { slug, timeSlotStartsAt, dateSuggested, bookingProposalItems } = proposal;

  const dateLabel = getProposalDateLabel({ timeSlotStartsAt, dateSuggested });
  const itemsByCar = groupBookingProposalItemsByCar(bookingProposalItems);

  return (
    <NoddiBasicCard
      to={customerRoutes.bookingProposalLandingPage.getPath({ slug })}
      className='flex flex-col gap-3 p-4'
    >
      {dateLabel && (
        <div className='flex items-center gap-2 text-sm text-muted-foreground'>
          <NoddiIcon name='Calendar' size='small' />
          <span>{dateLabel}</span>
        </div>
      )}
      {itemsByCar.length > 0 && (
        <ul className='flex flex-col gap-2 text-sm'>
          {itemsByCar.map(({ car, items }) => (
            <li key={car.id} className='flex flex-col gap-1'>
              <div className='flex items-center gap-2 text-muted-foreground'>
                <NoddiIcon name='Car' size='small' />
                <span>
                  {car.make} – {formatLicensePlate(car.licensePlate.number)}
                </span>
              </div>
              {items.length > 0 && (
                <ul className='ml-6 list-disc text-xs text-muted-foreground'>
                  {items.flatMap((item) =>
                    item.salesItemsWithQuantity.map((sales) => (
                      <li key={`${item.id}-${sales.salesItem.id}`}>
                        {sales.quantity > 1 ? `${sales.quantity}× ` : ''}
                        {sales.salesItem.name}
                      </li>
                    ))
                  )}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}
      <div className='flex justify-end'>
        <NoddiDummyButton size='small' variant='success' endIcon='ArrowRight'>
          <Trans>Book this too</Trans>
        </NoddiDummyButton>
      </div>
    </NoddiBasicCard>
  );
};
